if (!window.__missenceMobileMenu) {
  window.__missenceMobileMenu = true;
  const TRANSITION_MS = 260;
  const OPEN_CLASS = 'is-active';
  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileViewport = window.matchMedia('(max-width: 989px)');
  const controllers = new Map();

  const createController = (menu) => {
    const toggle = menu.querySelector('[data-missence-mobile-menu-toggle]');
    const panel = menu.querySelector('[data-missence-mobile-menu-panel]');
    if (!toggle || !panel) return null;

    let inertElements = [];
    let closeTimer = null;
    let expanded = false;

    const isOpen = () => expanded;

    const getFocusable = () => [
      toggle,
      ...Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => element.offsetParent !== null),
    ];

    const setPageInert = (value) => {
      if (!value) {
        inertElements.forEach((element) => {
          element.inert = false;
        });
        inertElements = [];
        return;
      }

      let node = menu;
      while (node.parentElement && node !== document.body) {
        Array.from(node.parentElement.children).forEach((sibling) => {
          if (sibling === node || sibling.inert || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) return;
          sibling.inert = true;
          inertElements.push(sibling);
        });
        node = node.parentElement;
      }
    };

    const setExpanded = (value) => {
      toggle.setAttribute('aria-expanded', value ? 'true' : 'false');
      const label = value ? toggle.dataset.labelClose : toggle.dataset.labelOpen;
      if (label) toggle.setAttribute('aria-label', label);
      document.body.classList.toggle('overflow-hidden', value);
      document.body.classList.toggle('missence-mobile-menu-open', value);
    };

    const open = () => {
      if (expanded) return;
      expanded = true;
      window.clearTimeout(closeTimer);
      menu.open = true;
      setExpanded(true);
      setPageInert(true);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!expanded) return;
          menu.classList.add(OPEN_CLASS);
          getFocusable()[1]?.focus({ preventScroll: true });
        });
      });
    };

    const close = ({ returnFocus = true } = {}) => {
      if (!expanded) return;
      expanded = false;
      menu.classList.remove(OPEN_CLASS);
      setExpanded(false);
      setPageInert(false);
      if (returnFocus) toggle.focus({ preventScroll: true });

      window.clearTimeout(closeTimer);
      if (reducedMotion.matches) {
        menu.open = false;
        return;
      }
      closeTimer = window.setTimeout(() => {
        if (!expanded) menu.open = false;
      }, TRANSITION_MS);
    };

    const onToggleClick = (event) => {
      event.preventDefault();
      if (isOpen()) {
        close();
      } else {
        open();
      }
    };

    const onPanelClick = (event) => {
      if (event.target.closest('a[href]')) close({ returnFocus: false });
    };

    toggle.addEventListener('click', onToggleClick);
    panel.addEventListener('click', onPanelClick);

    const trapFocus = (event) => {
      const focusable = getFocusable();
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!menu.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const destroy = () => {
      close({ returnFocus: false });
      window.clearTimeout(closeTimer);
      toggle.removeEventListener('click', onToggleClick);
      panel.removeEventListener('click', onPanelClick);
    };

    return { menu, isOpen, open, close, trapFocus, destroy };
  };

  const initMobileMenus = (scope = document) => {
    scope.querySelectorAll('[data-missence-mobile-menu]').forEach((menu) => {
      if (controllers.has(menu)) return;
      const controller = createController(menu);
      if (controller) controllers.set(menu, controller);
    });
  };

  const destroyMobileMenus = (scope) => {
    controllers.forEach((controller, menu) => {
      if (!scope.contains(menu)) return;
      controller.destroy();
      controllers.delete(menu);
    });
  };

  const getOpenController = () => Array.from(controllers.values()).find((controller) => controller.isOpen());

  document.addEventListener('keydown', (event) => {
    const controller = getOpenController();
    if (!controller) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      controller.close();
    } else if (event.key === 'Tab') {
      controller.trapFocus(event);
    }
  });

  document.addEventListener('click', (event) => {
    const controller = getOpenController();
    if (controller && !controller.menu.contains(event.target)) controller.close({ returnFocus: false });
  });

  mobileViewport.addEventListener?.('change', (event) => {
    if (!event.matches) getOpenController()?.close({ returnFocus: false });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initMobileMenus());
  } else {
    initMobileMenus();
  }

  document.addEventListener('shopify:section:load', (event) => initMobileMenus(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyMobileMenus(event.target));

  document.addEventListener('shopify:block:select', (event) => {
    const menu = event.target.closest('[data-missence-mobile-menu]');
    if (menu && mobileViewport.matches) controllers.get(menu)?.open();
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const menu = event.target.closest('[data-missence-mobile-menu]');
    if (menu) controllers.get(menu)?.close({ returnFocus: false });
  });
}
